import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Sparkles, BellRing, ArrowRight, Smartphone, ShieldCheck } from "lucide-react";
import { useAuth } from "../lib/auth";

export default function ComingSoon() {
  const { isAuthed, requireAuth } = useAuth();
  const navigate = useNavigate();

  const onCTA = () => {
    if (!isAuthed) {
      requireAuth({ redirectTo: "/listings" });
      return;
    }
    navigate("/listings");
  };

  return (
    <section id="download" className="section">
      <div className="container-z">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.55 }}
          className="zcard overflow-hidden relative"
          data-testid="coming-soon-section"
        >
          <div className="grid lg:grid-cols-2">
            {/* Left: Badge + copy */}
            <div className="p-8 sm:p-12 lg:p-14 relative">
              <div className="absolute -top-12 -left-12 w-48 h-48 rounded-full bg-[#FBEFE9] opacity-70 pointer-events-none" />
              <div className="relative">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#C86646] text-white text-[11px] font-bold tracking-[0.22em] uppercase">
                  <Sparkles className="w-3.5 h-3.5" />
                  Coming Soon
                </div>
                <h2
                  className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#1A231E] leading-[1.05]"
                  data-testid="coming-soon-headline"
                >
                  Mobile App<br /><span className="text-[#C86646]">Coming Soon!</span>
                </h2>
                <p className="mt-4 text-base text-[#5C6B62] max-w-md leading-relaxed">
                  The Zumroo Android &amp; iOS apps are in private beta. For now, the full
                  experience — browse, search, save, and unlock owner contacts — is live on
                  the web. Your account will carry over the day the apps launch.
                </p>

                <div className="mt-7 flex flex-wrap items-center gap-3">
                  <button
                    onClick={onCTA}
                    className="btn-primary"
                    data-testid="coming-soon-cta"
                  >
                    Start on the web
                    <ArrowRight className="w-4 h-4" />
                  </button>
                  <span className="text-sm text-[#5C6B62] flex items-center gap-1.5">
                    <BellRing className="w-4 h-4 text-[#C86646]" />
                    We'll email you when the apps go live.
                  </span>
                </div>

                <div className="mt-7 flex items-center gap-3 text-sm text-[#5C6B62]">
                  <ShieldCheck className="w-4 h-4 text-[#2D6A4F]" />
                  Same login. Same data. Web first, mobile soon.
                </div>
              </div>
            </div>

            {/* Right: Forest tile with phone silhouette + Coming Soon badge */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.15, duration: 0.5 }}
              className="relative bg-gradient-to-br from-[#1E3F33] to-[#14322A] p-10 sm:p-14 flex items-center justify-center min-h-[280px]"
            >
              <div className="absolute inset-0 opacity-10 bg-paper" />
              <div className="relative flex flex-col items-center text-center">
                <div className="w-28 h-28 rounded-3xl bg-white/10 border border-white/15 flex items-center justify-center backdrop-blur-sm">
                  <Smartphone className="w-12 h-12 text-white/90" />
                </div>
                <div className="mt-5 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#C86646] text-white text-[11px] font-bold tracking-wider uppercase">
                  <Sparkles className="w-3 h-3" /> Coming Soon
                </div>
                <div className="mt-4 font-display text-2xl font-bold text-white tracking-tight">
                  Mobile App
                </div>
                <div className="mt-1 text-sm text-white/70">
                  Android &amp; iOS launching together
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
